// MO4.1
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/ArrowUp';
import Flex from '@wix/da-ds/pkg/Flex';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { hashString } from '../../../utils/hash';
import Dumbo from '../../Dumbo';
import TierStatisticsBar from '../../Helper/StatisticsBar/TierStatisticsBar';
import PriceWithDiscount from '../../../PriceWithDiscount';
import DiscountsPopupTrigger from '../../../DiscountsPopupTrigger';
import Benefits from '../Benefits';
import { formatPrice } from '../utils';
import { Props } from '../TierProductCardDumb';
import s from './TierProductCardDumbDesktop.scss';

const coverWidth = parseInt(s['cover-width']);
const coverHeight = parseInt(s['cover-height']);

const TierProductCardDumbDesktop: React.FC<Props> = ({
  coverImage,
  showMatureCover,
  title,
  stats,
  dataHook,
  description,
  price = '',
  discount,
  allDiscounts = [],
  benefits = [],
  hasDiscord,
  info,
  ctaButton,
  secondaryCtaButton,
  withExpandButton,
  coverAreaExtrasLeft,
  coverAreaExtrasRight,
  isHighlighted,
  showEmptyPlaceholders,
  className,
  onClick,
  onToggleCollapsed,
  isCollapsed: isCollapsedProp,
  innerRef,
}) => {
  const { t } = useTranslation();
  const [isCollapsedState, setIsCollapsed] = useState(false);
  const isControlled = isCollapsedProp !== undefined;
  const isCollapsed = isControlled ? isCollapsedProp : isCollapsedState;
  const hasBenefits = benefits.length > 0 || hasDiscord;
  const benefitsHash = hashString(`benefits.${benefits.join('|')}`);

  const toggleCollapsed = useCallback(() => {
    if (isControlled) {
      onToggleCollapsed && onToggleCollapsed(!isCollapsed);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  }, [onToggleCollapsed, isControlled, isCollapsed]);

  return (
    <Flex
      className={classnames(
        s['root'],
        'ds-card',
        isHighlighted && 'ds-card-selected',
        onClick && s['clickable-root'],
        className
      )}
      direction="column"
      onClick={onClick}
      innerRef={innerRef}
      dataHook={dataHook}
    >
      <div className={s['cover-container']}>
        {coverAreaExtrasLeft && (
          <div className={s['cover-area-extras-left']}>{coverAreaExtrasLeft}</div>
        )}
        {coverAreaExtrasRight && (
          <div className={s['cover-area-extras-right']}>{coverAreaExtrasRight}</div>
        )}
        {coverImage ? (
          <Dumbo
            width={coverWidth}
            height={coverHeight}
            className={s['cover']}
            deviation={coverImage}
            showMatureContent={showMatureCover}
          />
        ) : (
          <div className={classnames(s['cover'], s['placeholder-cover'])} />
        )}
      </div>
      {title && <Heading className={s['title']}>{title}</Heading>}
      {!title && showEmptyPlaceholders && (
        <div className={classnames(s['placeholder'], s['placeholder-title'])} />
      )}

      <TierStatisticsBar className={s['stats']} stats={stats} />

      {price && (
        <div className={s['price-block']}>
          {discount && (
            <PriceWithDiscount
              dollarAmount={price}
              eligibleDiscount={discount}
              textVariant="subscription"
              infix={
                <span className={s['price-period']}>
                  /{t('deviationview.tierCard.pricePeriod.month')}
                </span>
              }
            />
          )}
          {!discount && (
            <>
              <span className={s['price']}>{formatPrice(price)}</span>
              <span className={s['price-period']}>
                /{t('deviationview.tierCard.pricePeriod.month')}
              </span>
            </>
          )}
          {allDiscounts.length > 0 && (
            <DiscountsPopupTrigger
              className={s['toggle-discounts-popup']}
              allDiscounts={allDiscounts}
              eligibleDiscount={discount}
            />
          )}
        </div>
      )}
      {!price && showEmptyPlaceholders && (
        <div className={classnames(s['placeholder'], s['placeholder-price'])} />
      )}

      {ctaButton && (
        <div className={s['cta-buttons']}>
          {ctaButton}
          {secondaryCtaButton}
        </div>
      )}

      {description && (
        <div
          className={classnames(
            s['description'],
            (!hasBenefits || isCollapsed) && s['no-benefits']
          )}
        >
          {description}
        </div>
      )}
      {!description && showEmptyPlaceholders && (
        <div className={s['placeholder-description']}>
          <div className={classnames(s['placeholder'], s['placeholder-line'])} />
          <div className={classnames(s['placeholder'], s['placeholder-line'])} />
          <div className={classnames(s['placeholder'], s['placeholder-line'])} />
        </div>
      )}

      <div className={classnames(s['collapsible'], isCollapsed && s['collapsed'])}>
        {(hasBenefits || showEmptyPlaceholders) && (
          <Benefits
            key={benefitsHash}
            benefits={benefits}
            showPlaceholder={showEmptyPlaceholders}
            hasDiscord={hasDiscord}
          />
        )}
      </div>

      <div className={s['footer']}>
        {info && (
          <>
            <hr className={s['divider']} />
            <Flex className={s['info-block']} alignItems="flex-start">
              <InfoIcon size={IconSize.SMALLER} className={s['info-icon']} disableDefaultColors />
              <div className={s['info']}>{info}</div>
            </Flex>
          </>
        )}

        {withExpandButton && hasBenefits && (
          <>
            <hr className={s['divider']} />
            <PlainButtonOrLink onClick={toggleCollapsed} className={s['toggle-button']}>
              <span className={s['toggle-button-label']}>
                {isCollapsed
                  ? t('deviationview.tierCard.showMore')
                  : t('deviationview.tierCard.showLess')}
              </span>
              {isCollapsed ? (
                <ArrowDownIcon size={IconSize.SMALLER} disableDefaultColors />
              ) : (
                <ArrowUpIcon size={IconSize.SMALLER} disableDefaultColors />
              )}
            </PlainButtonOrLink>
          </>
        )}
      </div>
    </Flex>
  );
};
TierProductCardDumbDesktop.displayName = 'TierProductCardDumbDesktop';

export default TierProductCardDumbDesktop;
